import { makeStyles } from '@material-ui/styles'
import React from 'react'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import GlobalDropdownWithSearch from 'core/elements/dropdown/GlobalDropdownWithSearch'

const useStyles = makeStyles((theme: any) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    marginRight: theme.spacing(1),
  },
  filterContainer: {
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
    borderRight: `1px solid ${theme.palette.divider}`,

    '&:first-child': {
      paddingLeft: 0,
    },
    '& .placeholder': {
      minHeight: '34px',
    },
  },
  filterDropdown: {
    width: 150,
    minWidth: 100,
    maxWidth: 200,
  },
  dropdownWithRemove: {
    position: 'relative',
    '& .dropdownInputFrame': {
      paddingRight: 48,
    },
  },
  removeIcon: {
    position: 'absolute',
    right: 26,
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    background: 'transparent',
    border: 'none',
    zIndex: 1,

    '&:hover': {
      color: theme.palette.grey[700],
    },
  },
}))

export interface FilterOption {
  value: string
  label: string
}

export interface FilterConfig {
  /** Unique key for this filter (used in API params) */
  key: string
  /** Display label for the filter */
  label: string
  /** Available options for this filter (not needed if using customComponent) */
  options?: FilterOption[]
  /** Default value (usually 'all') */
  defaultValue: string
  /** Custom component to render instead of default dropdown */
  customComponent?: React.ComponentType<CustomFilterComponentProps>
  /** Whether this filter should be visible (default: true) */
  visible?: boolean
  /** Label position: 'top' (above) or 'left' (beside) - default: 'top' */
  labelPosition?: 'top' | 'left'
}

export interface CustomFilterComponentProps {
  /** Current value of the filter */
  value: string
  /** Callback when value changes */
  onChange: (value: string) => void
  /** Whether the filter is disabled */
  disabled?: boolean
  /** CSS class name for styling */
  className?: string
  /** Label position: 'top' or 'left' */
  labelPosition?: 'top' | 'left'
  /** Width of the dropdown */
  width?: number
}

export interface ServerSideFiltersProps {
  /** Filter configurations */
  filters: FilterConfig[]
  /** Current filter values */
  values: Record<string, string>
  /** Callback when a filter value changes */
  onChange: (key: string, value: string) => void
  /** Callback when a filter is removed (if provided, shows X button) */
  onRemove?: (key: string) => void
  /** Disabled state */
  disabled?: boolean
}

/**
 * Generic server-side filters component.
 *
 * Renders a row of dropdown filters based on the provided configuration.
 * Each filter has a key that maps to an API query parameter.
 */
const ServerSideFilters: React.FC<ServerSideFiltersProps> = ({
  filters,
  values,
  onChange,
  onRemove,
  disabled = false,
}) => {
  const classes = useStyles()

  // Filter out invisible filters. Keeping the check !== false for backward compatibility
  const visibleFilters = filters.filter((filter) => filter.visible !== false)

  const wrapWithRemove = (filterKey: string, filterLabel: string, child: React.ReactNode) => (
    <div key={filterKey} className={classes.filterContainer}>
      <div className={classes.dropdownWithRemove}>
        {child}
        <button
          className={classes.removeIcon}
          onClick={(e) => {
            e.stopPropagation()
            onRemove(filterKey)
          }}
          title="Remove filter"
          aria-label={`Remove ${filterLabel} filter`}
        >
          <FontAwesomeIcon size="lg">times</FontAwesomeIcon>
        </button>
      </div>
    </div>
  )

  return (
    <div className={classes.root}>
      {visibleFilters.map((filter) => {
        const currentValue = values[filter.key] ?? filter.defaultValue ?? 'all'

        if (filter.customComponent) {
          const CustomComponent = filter.customComponent
          const component = (
            <CustomComponent
              key={filter.key}
              value={currentValue}
              onChange={(value) => onChange(filter.key, value)}
              disabled={disabled}
              className={classes.filterDropdown}
              labelPosition={filter.labelPosition}
              width={112}
            />
          )
          return onRemove ? (
            wrapWithRemove(filter.key, filter.label, component)
          ) : (
            <React.Fragment key={filter.key}>{component}</React.Fragment>
          )
        }

        const labelPosition = filter.labelPosition ?? 'top'
        const dropdown = (
          <GlobalDropdownWithSearch
            key={filter.key}
            label={filter.label}
            items={filter.options || []}
            onChange={(item) => onChange(filter.key, item.value)}
            value={currentValue}
            {...(labelPosition === 'left' ? { showLabelOnLeft: true } : { showLabelOnTop: true })}
            align="left"
            disabled={disabled}
            width={112}
          />
        )
        return onRemove ? (
          wrapWithRemove(filter.key, filter.label, dropdown)
        ) : (
          <React.Fragment key={filter.key}>{dropdown}</React.Fragment>
        )
      })}
    </div>
  )
}

export default ServerSideFilters
